import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStaffList, useDeleteStaff } from '@/hooks/api'
import { STAFF_PER_PAGE } from './StaffPage.static'
import type { GetStaffParams } from '@/services/api/staff/staff.static'

export function useStaffPage() {
  const [page, setPage] = useState(1)
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [showArchive, setShowArchive] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>('')
  const { t } = useTranslation('staff')
  const deleteStaffMutation = useDeleteStaff()

  const handleSearch = () => {
    setSearch(inputValue)
    setPage(1)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleClearSearch = () => {
    setInputValue('')
    setSearch('')
    setPage(1)
  }

  const params: GetStaffParams = {
    page,
    limit: STAFF_PER_PAGE,
    ...(search && { search }),
    ...(showArchive && { status: 'TERMINATED' as const }),
    ...(sortOrder && { sortOrder }),
  }

  const { data, isLoading, isError, refetch } = useStaffList(params)

  const staff = data?.staff ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / STAFF_PER_PAGE)
  const isEmpty = !isLoading && staff.length === 0

  const handleSetSortOrder = (val: 'asc' | 'desc' | '') => {
    setSortOrder(val)
    setPage(1)
  }

  const handleToggleArchive = () => {
    setShowArchive((prev) => !prev)
    setPage(1)
  }

  const handleDeleteStaff = () => {
    if (!deleteTargetId) return
    deleteStaffMutation.mutate(deleteTargetId, {
      onSettled: () => setDeleteTargetId(null),
    })
  }

  return {
    t,
    staff,
    total,
    page,
    totalPages,
    inputValue,
    search,
    showArchive,
    sortOrder,
    isLoading,
    isError,
    isEmpty,
    deleteTargetId,
    refetch,
    setInputValue,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,
    setPage,
    handleSetSortOrder,
    handleToggleArchive,
    setDeleteTargetId,
    handleDeleteStaff,
  }
}
